// src/pages/LeagueDashboard.js - COMPLETE WITH LEAGUE PLAYER SEARCH
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Crown, Users, Trophy, BarChart3, Calendar, MessageSquare,
  Settings, FileText, Star, Target, TrendingUp,
  Home, UserPlus, LogOut, Menu, X, ChevronRight,
  Search, UserCheck, Shield, Activity, DollarSign,
  ArrowRightLeft, Gavel, AlertCircle, User, History, FileEdit,
  ListFilter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PlayerModalProvider } from '../contexts/PlayerModalContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import NotificationBell from '../components/NotificationBell';
import { leaguesAPI } from '../services/apiService';
import apiService from '../services/apiService';
import { dynastyTheme } from '../services/colorService';
import { getTotalSalaryCap } from '../services/contractUtils';
import { useCommissioner } from '../contexts/CommissionerContext';
import LeagueSettings from '../components/LeagueSettings';

// Import the league-specific player search
import PlayerSearchDropdownLeague from '../components/PlayerSearchDropdownLeague';
import PositionAssignmentDropdown from '../components/league-dashboard/PositionAssignmentDropdown';
import RosterPlanModal from '../components/league-dashboard/RosterPlanModal';
import { useRosterPlan } from '../hooks/rosterPlan';
import { analyzeRosterCapacity } from '../utils/RosterCapacityUtils';

// Import the modular components
import LeagueHome from './league-dashboard/LeagueHome';
import LeagueOwners from './league-dashboard/LeagueOwners';
import LeagueStandings from './league-dashboard/LeagueStandings';
import ComingSoon from './league-dashboard/ComingSoon';
import TeamHomeDashboard from './league-dashboard/TeamHomeDashboard';
import FreeAgentSearch from './league-dashboard/FreeAgentSearch';
import MyRoster from './league-dashboard/MyRoster';
import TeamStats from './league-dashboard/TeamStats';
import SalaryContractSettings from './league-dashboard/salary-contract/SalaryContractSettings';
import TransactionLog from './league-dashboard/TransactionLog';
import CommissionerControls from './league-dashboard/CommissionerControls';
import TeamSetup from './league-dashboard/TeamSetup';
import { WatchList } from '../components/WatchList/WatchList';
import TradeCenter from '../components/trades/TradeCenter';
import ManualOverrides from './league-dashboard/ManualOverrides';
import KeeperSelection from './league-dashboard/KeeperSelection';
import BoxScore from '../components/BoxScore/BoxScore';
import ManualDailyUpdate from '../components/Admin/ManualDailyUpdate';
import ProbablePitchers from '../components/ProbablePitchers/ProbablePitchers';
import LineupHistory from '../components/LineupHistory/LineupHistory';
import MinorLeagueDraft from './league-dashboard/MinorLeagueDraft';
import MessageBoard from './league-dashboard/MessageBoard';
import TeamTransactions from './league-dashboard/TeamTransactions';
import DraftManagement from './league-dashboard/DraftManagement';
import DraftSetupPage from './league-dashboard/DraftSetupPage';
import Waivers from './league-dashboard/Waivers';
import LiveScoring from './league-dashboard/LiveScoring';
import OptimizerDashboard from './league-dashboard/optimizer/OptimizerDashboard';

const LeagueDashboard = () => {
  const { leagueId } = useParams();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Call the hook unconditionally (React Hooks rule)
  const commissionerContext = useCommissioner();
  
  // Then safely extract the functions with null checks
  const setActiveTeamId = commissionerContext?.setActiveTeamId || null;
  const setActiveTeamName = commissionerContext?.setActiveTeamName || null;
  // Commissioner state for FA add path (provider mounted at App root,
  // useCommissioner throws if absent — context is guaranteed non-null here).
  const isCommissionerMode = commissionerContext.isCommissionerMode;
  const getTargetTeamId = commissionerContext.getTargetTeamId;
  
  // Position assignment modal state (MOVED TO TOP LEVEL)
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [playerForAssignment, setPlayerForAssignment] = useState(null);
  const [positionModalRoster, setPositionModalRoster] = useState([]);
  const [positionModalCapacity, setPositionModalCapacity] = useState(null);

  // Bug 1 follow-up (Bible § 4.5.20, 2026-05-15): every caller of
  // PositionAssignmentDropdown MUST wire onOpenDropShuffle to a
  // useRosterPlan instance so the "All Active Positions Full" empty-
  // state panel renders its prominent Drop CTA. This top-level shell
  // fires the modal from the top-bar PlayerSearchDropdownLeague global
  // search; pre-fix users were stuck on the warning text with no CTA.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: playerForAssignment,
    currentRoster: positionModalRoster,
    isCommissionerMode,
    getTargetTeamId,
  });
  
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [userTeam, setUserTeam] = useState(null);

  // Helper function to set CSS variables for team colors
  const setTeamColorCSSVariables = (colors) => {
    if (!colors || !colors.primary) return;
    
    const root = document.documentElement;
    root.style.setProperty('--team-primary', colors.primary);
    root.style.setProperty('--team-secondary', colors.secondary || '#1c1917');
    
    // Set derived colors for hover/dark/light variations
    root.style.setProperty('--team-primary-hover', colors.primary);
    root.style.setProperty('--team-primary-dark', colors.primary);
    root.style.setProperty('--team-primary-light', `${colors.primary}1A`);
    root.style.setProperty('--team-primary-glow', `${colors.primary}0D`);
    root.style.setProperty('--team-primary-highlight', `${colors.primary}40`);
  };

  // Set CSS variables when userTeam loads or changes
  useEffect(() => {
    if (userTeam?.team_colors) {
      setTeamColorCSSVariables(userTeam.team_colors);
    }
  }, [userTeam]);
  const [userProfile, setUserProfile] = useState(null);
  const [standings, setStandings] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('league-home');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [leagueBanner, setLeagueBanner] = useState(null);
  const [bannerConfig, setBannerConfig] = useState({
    height: 120,
    titleSize: 'text-3xl',
    titlePosition: 'left',
    titleStyle: 'font-bold'
  });
  
  // Track team being viewed from TeamLinkDropdown navigation
  const [viewingTeamId, setViewingTeamId] = useState(null);
  const [viewingTeamName, setViewingTeamName] = useState(null);
  const [selectedBoxScoreTeamId, setSelectedBoxScoreTeamId] = useState(null);

  // Draft notification badge count
  const [draftNotificationCount, setDraftNotificationCount] = useState(0);

  // Initial tab for Trade Center (set from URL params, e.g. ?section=trades&tab=inbox)
  const [initialTradeTab, setInitialTradeTab] = useState(null);

  // Notification deep-link entity IDs (Bible § 4.5.12.2). A notification action_url
  // carries the exact entity (?tradeId/?claimId/?playerId); the matching section
  // deep-links to that specific row. Absent → land on the section normally (no
  // fabricated entity, no wrong-row fallback).
  const [highlightTradeId, setHighlightTradeId] = useState(null);
  const [highlightClaimId, setHighlightClaimId] = useState(null);
  const [highlightPlayerId, setHighlightPlayerId] = useState(null);

  // Add global styles for glow effect
  useEffect(() => {
    const styleId = 'dynasty-global-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = dynastyTheme.utils.globalStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Handle URL query params (e.g. email links: ?section=trades&tab=inbox)
  // Runs on mount AND when search params change (bell notification clicks)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const tab = params.get('tab');
    // Entity deep-link IDs (Bible § 4.5.12.2). null when absent — no wrong-row fallback.
    const tradeId = params.get('tradeId');
    const claimId = params.get('claimId');
    const playerId = params.get('playerId');
    if (section === 'trades') {
      setActiveSection('trade-center');
      if (tab) setInitialTradeTab(tab);
      setHighlightTradeId(tradeId || null);
    } else if (section === 'minor-league-draft') {
      setActiveSection('minor-league-draft');
    } else if (section === 'waivers') {
      setActiveSection('waivers');
      setHighlightClaimId(claimId || null);
    } else if (section === 'roster') {
      setActiveSection('my-roster');
      setHighlightPlayerId(playerId || null);
    }
  }, [location.search]);

  // Handle navigation state for section switching AND team selection
  useEffect(() => {
    if (location.state?.activeSection) {
      console.log('Setting active section from navigation:', location.state.activeSection);
      
      // Map the section names correctly
      let sectionToSet = location.state.activeSection;
      if (sectionToSet === 'team-roster') {
        sectionToSet = 'my-roster';
      }
      
      setActiveSection(sectionToSet);
      
      // Handle team selection from TeamLinkDropdown
      if (location.state.targetTeamId && location.state.targetTeamName) {
        console.log('Setting target team from navigation:', {
          id: location.state.targetTeamId,
          name: location.state.targetTeamName
        });
        
        // Store the team being viewed
        setViewingTeamId(location.state.targetTeamId);
        setViewingTeamName(location.state.targetTeamName);
        
        // If user is commissioner and context is available, set active team for editing
        if (league?.role === 'commissioner') {
          try {
            if (typeof setActiveTeamId === 'function') {
              setActiveTeamId(location.state.targetTeamId);
            }
            if (typeof setActiveTeamName === 'function') {
              setActiveTeamName(location.state.targetTeamName);
            }
          } catch (error) {
            console.warn('Could not set commissioner context:', error);
          }
        }
      }
      
      // Clear the state to prevent repeat navigation
      window.history.replaceState(null, '');
    }
  }, [location.state, league?.role]);

  // Load league banner
  useEffect(() => {
    if (leagueId) {
      loadLeagueBanner();
    }
  }, [leagueId]);

  const loadLeagueBanner = async () => {
    try {
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      if (response && response.success && response.settings?.league_banner_url) {
        setLeagueBanner(response.settings.league_banner_url);
        
        // Load banner config if exists
        if (response.settings.banner_config) {
          const config = response.settings.banner_config;
          setBannerConfig({
            height: config.height || 120,
            titleSize: config.titleSize || 'text-3xl',
            titlePosition: config.titlePosition || 'left',
            titleStyle: config.titleStyle || 'font-bold'
          });
        }
      }
    } catch (error) {
      console.log('Could not load league banner:', error);
    }
  };

  // Load user profile picture from MyAccount
  useEffect(() => {
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      const response = await apiService.get('/api/account/profile');
      if (response.data && response.data.profile) {
        setUserProfile({
          profilePicture: response.data.profile.picture || null,
          firstName: response.data.profile.given_name || user?.given_name || '',
          lastName: response.data.profile.family_name || user?.family_name || '',
          email: response.data.profile.email || user?.email || ''
        });
      }
    } catch (error) {
      console.log('Could not load user profile picture:', error);
    }
  };

  // Poll for draft notification badge count (every 30s when minor leagues enabled)
  useEffect(() => {
    if (!league?.include_minor_leagues || !leagueId) return;

    const fetchCount = async () => {
      try {
        const res = await leaguesAPI.getDraftNotificationCount(leagueId);
        setDraftNotificationCount(res.unread_count || 0);
      } catch {
        // Silently ignore — badge is non-critical
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [league?.include_minor_leagues, leagueId]);

  // Clear badge when user navigates to draft page
  useEffect(() => {
    if (activeSection === 'minor-league-draft' && draftNotificationCount > 0) {
      setDraftNotificationCount(0);
    }
  }, [activeSection, draftNotificationCount]);

  // Check if league needs setup attention
  const needsSetupAttention = () => {
    return league?.role === 'commissioner' &&
           (league?.league_status === 'setup' ||
            league?.league_status === 'pricing' ||
            league?.league_status === 'draft');
  };

  // ========================================
  // NAVIGATION WITH CONDITIONAL SALARY SETTINGS
  // ========================================
  const getNavigationSections = () => {
    const sections = [
      {
        title: 'LEAGUE',
        items: [
          { id: 'league-home', label: 'League Home', icon: Home },
          { id: 'standings', label: 'Standings', icon: Trophy },
          { id: 'transactions', label: 'Transactions', icon: Activity },
          { id: 'live-scoring', label: 'Live Scoring', icon: TrendingUp },
          { id: 'messages', label: 'League Chat', icon: MessageSquare },
        ]
      },
      {
        title: 'CLUBHOUSE',
        items: [
          { id: 'team-home', label: 'Team Dashboard', icon: Users },
          { id: 'team-stats', label: 'Team Stats', icon: BarChart3 },
          { id: 'my-roster', label: 'My Roster', icon: UserCheck },
          { id: 'probable-pitchers', label: 'Probable Pitchers', icon: Target },
          { id: 'daily-box-scores', label: 'Daily Box Scores', icon: Calendar },
          { id: 'lineup-history', label: 'Lineup History', icon: History },
          { id: 'team-setup', label: 'Team Setup', icon: Settings },
        ]
      },
      {
        title: 'FRONT OFFICE',
        items: [
          { id: 'free-agents', label: 'Free Agents', icon: Search },
          { id: 'waivers', label: 'Waivers', icon: ListFilter },
          { id: 'trade-center', label: 'Trade Center', icon: ArrowRightLeft },
          { id: 'team-transactions', label: 'Team Transactions', icon: DollarSign },
          { id: 'draft', label: 'Draft', icon: Gavel },
          { id: 'off-season', label: 'Off Season', icon: FileText },
          { id: 'watch-list', label: 'Watch List', icon: Star },
          { id: 'optimizer', label: 'Roster Optimizer', icon: Target },
          // Only show minor league draft if enabled in league settings
          ...(league?.league_settings?.include_minor_leagues ? [{ id: 'minor-league-draft', label: 'Minor League Draft', icon: Target, needsAttention: draftNotificationCount > 0, badge: draftNotificationCount }] : []),
        ]
      },
    ];

    // Only show admin section if user is commissioner
    if (league?.role === 'commissioner') {
      const commissionerItems = [
        { id: 'league-owners', label: 'Manage Owners', icon: UserPlus },
        { id: 'set-up', label: 'League Settings', icon: Settings },
      ];
      
      // ADD SALARY SETTINGS IF ENABLED
      if (league?.salary_settings?.use_salaries) {
        commissionerItems.push({ 
          id: 'salary-settings', 
          label: 'Salary/Contract Settings', 
          icon: DollarSign
        });
      }
      
      commissionerItems.push({
        id: 'draft-setup',
        label: 'Draft Setup',
        icon: FileText
      });
      commissionerItems.push({ 
        id: 'season-setup',
        label: 'Season Setup',
        icon: Gavel,
        needsAttention: needsSetupAttention()
      });
      commissionerItems.push({ id: 'manual-overrides', label: 'Manual Overrides', icon: FileEdit });
      commissionerItems.push({ id: 'admin-tools', label: 'Admin Tools', icon: Shield });
      
      sections.push({
        title: 'COMMISSIONER',
        items: commissionerItems
      });
    }

    return sections;
  };

  // ========================================
  // DATA LOADING - WITH PROPER ERROR HANDLING
  // ========================================
  useEffect(() => {
    if (leagueId) {
      loadLeagueData();
    }
  }, [leagueId, reloadTrigger]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      
      // Track if we found user's team (to prevent fallback overwrites)
      let userTeamFound = false;
      
      // Load league details with error handling
      let leagueData = null;
      try {
        const leagueResponse = await leaguesAPI.getLeagueDetails(leagueId);
        if (leagueResponse.success) {
          leagueData = leagueResponse.league;
          setLeague(leagueData);
        }
      } catch (leagueError) {
        console.error('Failed to load league details:', leagueError);
        
        // If league not found or access denied, show error and redirect
        if (leagueError.response?.status === 404) {
          setLoadError('League not found. It may have been deleted or the ID is incorrect.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                message: 'League not found or you do not have access to this league.',
                type: 'error'
              }
            });
          }, 3000);
          return;
        } else if (leagueError.response?.status === 403) {
          setLoadError('You do not have access to this league.');
          setTimeout(() => {
            navigate('/dashboard', { 
              state: { 
                message: 'Access denied. You are not a member of this league.',
                type: 'error'
              }
            });
          }, 3000);
          return;
        }
        
        // For other errors, try to continue with limited functionality
        setLoadError('Some features may be limited due to connection issues.');
      }

      // Try to get user's team directly
      try {
        console.log('🔍 DEBUG: Attempting to fetch user team for league:', leagueId);
        console.log('🔍 DEBUG: Current user:', user);
        
        const userTeamResponse = await leaguesAPI.getUserTeam(leagueId);
        
        console.log('🔍 DEBUG: getUserTeam raw response:', userTeamResponse);
        console.log('🔍 DEBUG: Response has success?', userTeamResponse?.success);
        console.log('🔍 DEBUG: Response has team_id?', userTeamResponse?.team_id);
        console.log('🔍 DEBUG: Full response structure:', JSON.stringify(userTeamResponse, null, 2));
        
        if (userTeamResponse.success && userTeamResponse.team_id) {
          console.log('✅ DEBUG: Setting userTeam to:', userTeamResponse);
          setUserTeam(userTeamResponse);
          userTeamFound = true; // ← Prevent fallbacks from overwriting
        } else {
          console.log('❌ DEBUG: Response missing success or team_id - will fall through to fallbacks');
          console.log('❌ DEBUG: success value:', userTeamResponse?.success);
          console.log('❌ DEBUG: team_id value:', userTeamResponse?.team_id);
        }
      } catch (teamError) {
        console.log('❌ DEBUG: getUserTeam threw error:', teamError);
        console.log('❌ DEBUG: Error response:', teamError.response);
        console.log('❌ DEBUG: Error status:', teamError.response?.status);
        console.log('❌ DEBUG: Error data:', teamError.response?.data);
      }

      // Load owner management data
      try {
        setOwnersLoading(true);
        const ownersResponse = await leaguesAPI.getLeagueOwners(leagueId);
        if (ownersResponse.success) {
          setOwners(ownersResponse.owners || []);
          
          // Fallback: Find user's team from owners data
          if (!userTeamFound && ownersResponse.owners?.length > 0) {
            const ownerWithTeam = ownersResponse.owners.find(
              owner => owner.user_id === user?.user_id || owner.email === user?.email
            );
            if (ownerWithTeam && ownerWithTeam.team_id) {
              setUserTeam({
                team_id: ownerWithTeam.team_id,
                team_name: ownerWithTeam.team_name || `Team ${ownerWithTeam.slot_number}`,
                team_logo_url: ownerWithTeam.team_logo_url,
                team_colors: ownerWithTeam.team_colors,
                manager_name: ownerWithTeam.manager_name,
                user_id: user?.user_id
              });
              userTeamFound = true; // Mark team as found via owners fallback
            }
          }
        }
      } catch (ownersError) {
        console.log('Owner data not available:', ownersError);
        setOwners([]);
      } finally {
        setOwnersLoading(false);
      }

      // Load league standings
      try {
        const standingsResponse = await leaguesAPI.getLeagueStandings(leagueId);
        if (standingsResponse.success) {
          setStandings(standingsResponse.standings || []);
          setTeams(standingsResponse.teams || []);
          
          // Another fallback: Find user's team from standings/teams
          if (!userTeamFound && standingsResponse.teams?.length > 0) {
            const teamFromStandings = standingsResponse.teams.find(
              team => team.user_id === user?.user_id
            );
            if (teamFromStandings) {
              setUserTeam(teamFromStandings);
              userTeamFound = true; // Mark team as found via standings fallback
            }
          }
        }
      } catch (standingsError) {
        console.log('Standings not available yet');
        setStandings([]);
        setTeams([]);
      }

      // Final fallback for commissioner
      if (!userTeamFound && leagueData?.role === 'commissioner') {
        console.log('⚠️ DEBUG: Using commissioner fallback team');
        setUserTeam({
          team_id: 'commissioner-team',
          team_name: 'Commissioner Team',
          user_id: user?.user_id
        });
        userTeamFound = true; // Mark fallback team as set
      }

    } catch (error) {
      console.error('Unexpected error loading league data:', error);
      setLoadError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSignOut = async () => {
    await signOut();
  };

  const handleLeagueDeleted = (leagueName) => {
    navigate('/dashboard', { 
      state: { 
        message: `League "${leagueName}" has been permanently deleted.`,
        type: 'success'
      }
    });
  };

  const handlePlayerAdded = (player) => {
    console.log('Player added to team:', player);
    // Trigger a reload of league data
    setReloadTrigger(prev => prev + 1);
  };

  const handlePlayerDropped = (player) => {
    console.log('Player dropped from team:', player);
    // Trigger a reload of league data
    setReloadTrigger(prev => prev + 1);
  };

  // Handler for opening position assignment modal from search
  const handleOpenPositionModal = async (player) => {
    try {
      // Commissioner-aware roster fetch: load target team's roster, not user's,
      // so capacity analysis matches the team we're actually adding to.
      const targetTeamId = getTargetTeamId(userTeam?.team_id);
      const rosterResponse = isCommissionerMode && targetTeamId
        ? await leaguesAPI.getTeamRosterCanonical(leagueId, targetTeamId)
        : await leaguesAPI.getMyRosterCanonical(leagueId);

      if (rosterResponse.success && rosterResponse.players) {
        setPositionModalRoster(rosterResponse.players);
        const analysis = analyzeRosterCapacity(rosterResponse.players, league);
        setPositionModalCapacity(analysis);
        setPlayerForAssignment(player);
        setShowPositionModal(true);
      }
    } catch (error) {
      console.error('Failed to load roster for position assignment:', error);
    }
  };

  // Handler for completing position assignment
  const handleCompletePositionAssignment = async (assignmentData) => {
    const { player, assignment, roster_status } = assignmentData;

    // Bible Rule 4: canonical namespaces (ids, info, stats, league_context, pricing).
    // Bible Rule 9: missing required data is a bug — throw, don't silently default.
    const leaguePlayerId = player.ids?.league_player;
    if (!leaguePlayerId) {
      throw new Error(
        `Cannot add player: missing ids.league_player ` +
        `(mlb=${player.ids?.mlb}, name=${player.info?.full_name}). ` +
        `Player object likely came from a non-canonical source.`
      );
    }
    if (!roster_status) {
      throw new Error('Cannot add player: assignmentData.roster_status is required');
    }
    if (roster_status === 'active' && !assignment) {
      throw new Error(
        'Cannot add player to active roster: missing slot id (assignmentData.assignment)'
      );
    }

    // 4-branch contract resolution per Bible § 4.5 contract model:
    //   - Minors: zero out (no contract)
    //   - Offseason w/ projected_contract (line 1027): keeper rights
    //   - Owned in-season: existing roster_data.financial
    //   - FA pickup (default): baseline_price + league policy (§ 4.5.1 line 809, § 4.5.2 line 821)
    const isOffseason = ['setup', 'draft', 'finalizing'].includes(league?.league_status);
    const isOwnedAlready = player.league_context?.status === 'owned';
    const isMinors = roster_status === 'minors';
    const playerName = player.info?.full_name;

    let playerSalary;
    let contractYears;
    if (isMinors) {
      playerSalary = 0;
      contractYears = 0;
    } else if (isOffseason && player.projected_contract) {
      playerSalary = player.projected_contract.baseline_price;
      contractYears = player.projected_contract.contract_years;
      if (playerSalary == null) {
        throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
      }
      if (contractYears == null) {
        throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
      }
    } else if (isOwnedAlready) {
      playerSalary = player.league_context.financial?.contract_salary;
      contractYears = player.league_context.financial?.contract_years;
      if (playerSalary == null) {
        throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
      }
      if (contractYears == null) {
        throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
      }
    } else {
      // Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup price = baseline_price.
      // Bible § 4.5.2 line 821: contract_years = league.salary_settings.standard_contract_length.
      playerSalary = player.league_context?.financial?.baseline_price;
      contractYears = league?.salary_settings?.standard_contract_length;
      if (playerSalary == null) {
        throw new Error(
          `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
          `Pricing engine may not have run for this league.`
        );
      }
      if (contractYears == null) {
        throw new Error(
          `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
        );
      }
    }

    try {
      const payload = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status,
        roster_position: roster_status === 'active' ? assignment : null,
        start_contract: !isMinors
      };

      if (isCommissionerMode) {
        payload.commissioner_action = true;
        payload.target_team_id = getTargetTeamId(userTeam?.team_id);
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, payload);

      if (response.success) {
        setShowPositionModal(false);
        setPlayerForAssignment(null);
        setPositionModalRoster([]);
        setPositionModalCapacity(null);
        handlePlayerAdded(player);
      }
    } catch (error) {
      console.error('Failed to add player:', error);
      throw error; // Let modal handle error display
    }
  };

  const handleStatusChange = () => {
    console.log('League status changed, reloading data...');
    loadLeagueData();
  };

  // ========================================
  // USER PROFILE PICTURE COMPONENT
  // ========================================
  const UserProfilePicture = () => {
    const handleProfileClick = () => {
      navigate('/my-account');
    };

    const profileContent = userProfile?.profilePicture ? (
      <img 
        src={userProfile.profilePicture}
        alt={`${userProfile.firstName || user?.given_name || 'User'} Profile`}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.style.display = 'none';
          const fallbackDiv = e.target.parentElement.querySelector('.initials-fallback');
          if (fallbackDiv) {
            fallbackDiv.style.display = 'flex';
          }
        }}
      />
    ) : null;

    const initials = `${(userProfile?.firstName || user?.given_name || 'U')[0]}${(userProfile?.lastName || user?.family_name || 'U')[0]}`.toUpperCase();
    
    return (
      <button 
        onClick={handleProfileClick}
        className="flex items-center gap-2 group"
        title="Go to My Account"
      >
        <div className="relative">
          <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-yellow-400/50 shadow-xl transition-all duration-300 group-hover:border-yellow-400 group-hover:scale-105 group-hover:shadow-[0_0_20px_rgba(250,204,21,0.6)]">
            {profileContent}
            <div 
              className="initials-fallback w-full h-full flex items-center justify-center text-xl font-bold bg-gradient-to-br from-yellow-400 to-yellow-500 text-black"
              style={{ display: userProfile?.profilePicture ? 'none' : 'flex' }}
            >
              {initials}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start">
          <span className="text-xs text-yellow-400 font-semibold group-hover:text-yellow-300 transition-colors">
            My Account
          </span>
          <span className="text-[10px] text-neutral-400 group-hover:text-neutral-300 transition-colors">
            {userProfile?.firstName || user?.given_name || 'Profile'} {userProfile?.lastName?.[0] || user?.family_name?.[0] || ''}.
          </span>
        </div>
      </button>
    );
  };

  // ========================================
  // CONTENT RENDERING WITH PROPER PROPS
  // ========================================
  const renderContent = () => {
    // Show error state if critical error occurred
    if (loadError && !league) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
            Error Loading League
          </h2>
          <p className={`${dynastyTheme.classes.text.neutralLight} mb-4 text-center max-w-md`}>
            {loadError}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }

    const sharedProps = {
      league,
      leagueId,
      user,
      teams,
      standings,
      owners,
      ownersLoading,
      loadLeagueData,
      setActiveSection,
      userTeam,
      leagueBanner,
      bannerConfig
    };

    switch (activeSection) {
      case 'league-home':
        return (
          <LeagueHome 
            {...sharedProps} 
            onSelectBoxScoreTeam={setSelectedBoxScoreTeamId}
          />
        );
      
      case 'standings':
        return <LeagueStandings {...sharedProps} />;
      
      case 'team-stats':
        return (
          <TeamStats
            leagueId={leagueId}
            league={league}
            user={user}
            teams={teams.length > 0 ? teams : owners}
            userTeam={userTeam}
            // Pass viewing team info if navigated from TeamLinkDropdown
            initialViewTeamId={viewingTeamId}
            initialViewTeamName={viewingTeamName}
            // Pass banner config for consistent styling with league settings
            bannerConfig={bannerConfig}
          />
        );
      
      case 'transactions':
        return (
        <TransactionLog
        leagueId={leagueId}
        leagueStatus={league?.league_status}
        league={league}
          leagueBanner={leagueBanner}
          bannerConfig={bannerConfig}
          allTeams={teams}
          />
        );
      
      case 'trade-center':
        return (
          <TradeCenter
            leagueId={leagueId}
            league={league}
            user={user}
            userTeam={userTeam}
            initialTradePlayer={location.state?.initialTradePlayer}
            initialTradeState={location.state?.initialTradeState}
            initialTab={initialTradeTab}
            highlightTradeId={highlightTradeId}
          />
        );
      
      case 'league-owners':
        return <LeagueOwners {...sharedProps} />;
      
      case 'team-home': {
        const teamToUse = userTeam || {
          team_id: `team-${user?.user_id}`,
          team_name: `${user?.firstName || user?.given_name || 'My'} Team`,
          user_id: user?.user_id
        };
        
        return (
          <TeamHomeDashboard
            teamId={teamToUse.team_id}
            leagueId={leagueId}
            currentUser={user}
            league={league}
            userTeam={teamToUse}
            bannerConfig={bannerConfig}
          />
        );
      }

      case 'free-agents':
        return (
          <FreeAgentSearch
            leagueId={leagueId}
            onPlayerAdded={handlePlayerAdded}
            league={league}
            user={user}
            userTeam={userTeam}
          />
        );

      case 'waivers':
        return (
          <Waivers
            leagueId={leagueId}
            userTeamId={userTeam?.team_id}
            league={league}
            userTeam={userTeam}
            highlightClaimId={highlightClaimId}
          />
        );

      case 'my-roster':
        return (
          <MyRoster
            leagueId={leagueId}
            league={league}
            user={user}
            onPlayerDropped={handlePlayerDropped}
            // Notification deep-link: highlight/scroll-to the specific player (Bible § 4.5.12.2)
            highlightPlayerId={highlightPlayerId}
            // Pass viewing team info if navigated from TeamLinkDropdown
            initialViewTeamId={viewingTeamId}
            initialViewTeamName={viewingTeamName}
            // Pass banner config for consistent styling with league settings
            bannerConfig={bannerConfig}
            // Pass userTeam for logo fallback (same as TeamStats)
            userTeam={userTeam}
          />
        );

      case 'team-setup':
        return (
          <TeamSetup 
            leagueId={leagueId}
            userTeam={userTeam}
            onTeamUpdated={loadLeagueData}
            user={user}
          />
        );
      
      case 'set-up':
        return (
          <LeagueSettings 
            leagueId={leagueId}
            user={user}
            onLeagueDeleted={handleLeagueDeleted}
          />
        );
      
      case 'salary-settings':
        return league?.role === 'commissioner' ? (
          <SalaryContractSettings 
            leagueId={leagueId}
            league={league}
            user={user}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <ComingSoon title="Unauthorized" description="Commissioner access only" />
        );
      
      case 'draft-setup':
        return league?.role === 'commissioner' ? (
          <DraftSetupPage
            leagueId={leagueId}
            league={league}
            teams={teams.length > 0 ? teams : owners}
          />
        ) : (
          <ComingSoon title="Unauthorized" description="Commissioner access only" />
        );

      case 'season-setup':
        return league?.role === 'commissioner' ? (
          <CommissionerControls
            leagueId={leagueId}
            league={league}
            onStatusChange={handleStatusChange}
          />
        ) : (
          <ComingSoon title="Unauthorized" description="Commissioner access only" />
        );

      case 'manual-overrides':
        return league?.role === 'commissioner' ? (
          <ManualOverrides
            leagueId={leagueId}
            league={league}
            teams={teams.length > 0 ? teams : owners}
          />
        ) : (
          <ComingSoon title="Unauthorized" description="Commissioner access only" />
        );
      
      // Watch List - Render inline with league context
      case 'watch-list':
        return (
          <WatchList 
            leagueId={leagueId}
            league={league}
            userTeam={userTeam}
          />
        );
      
      // Coming soon pages
      case 'live-scoring':
        return (
          <LiveScoring
            league={league}
            leagueId={leagueId}
            teams={teams.length > 0 ? teams : owners}
            userTeam={userTeam}
            leagueBanner={leagueBanner}
            bannerConfig={bannerConfig}
          />
        );
      case 'messages':
        return (
          <MessageBoard
            league={league}
            leagueId={leagueId}
            user={user}
            userTeam={userTeam}
            leagueBanner={leagueBanner}
            bannerConfig={bannerConfig}
          />
        );
      case 'probable-pitchers':
        return (
          <ProbablePitchers
            leagueId={leagueId}
            teamId={userTeam?.team_id}
            league={league}
            userTeam={userTeam}
          />
        );
      case 'daily-box-scores':
        return (
          <BoxScore
            leagueId={leagueId}
            teamId={selectedBoxScoreTeamId || userTeam?.team_id}
            bannerConfig={bannerConfig}
            userTeam={userTeam}
          />
        );
      case 'lineup-history':
        return (
          <LineupHistory
            leagueId={leagueId}
            teamId={userTeam?.team_id}
            league={league}
            userTeam={userTeam}
          />
        );
      case 'draft':
        return (
          <DraftManagement
            league={league}
            userTeam={userTeam}
          />
        );
      case 'off-season':
        return (
          <KeeperSelection
            leagueId={leagueId}
            league={league}
            user={user}
            userTeam={userTeam}
          />
        );
      case 'team-transactions':
        return (
          <TeamTransactions
            leagueId={leagueId}
            league={league}
            user={user}
            userTeam={userTeam}
            teams={teams}
          />
        );
      case 'minor-league-draft':
        return (
          <MinorLeagueDraft
            leagueId={leagueId}
            league={league}
            user={user}
            userTeam={userTeam}
          />
        );
      case 'admin-tools':
        return league?.role === 'commissioner' ? (
          <ManualDailyUpdate
            leagueId={leagueId}
            league={league}
            user={user}
          />
        ) : (
          <ComingSoon title="Unauthorized" description="Commissioner access only" />
        );
      case 'optimizer':
        return (
          <OptimizerDashboard
            leagueId={leagueId}
            league={league}
            user={user}
            userTeam={userTeam}
            teams={teams}
          />
        );
      default:
        return <ComingSoon title="Feature" description="This feature is coming soon" />;
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    return (
      <div className={dynastyTheme.components.page}>
        <div className="flex justify-center items-center min-h-screen">
          <div className="flex items-center space-x-3">
            <div 
              className={`w-8 h-8 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`}
            />
            <span className={`${dynastyTheme.classes.text.white} text-lg`}>Loading league...</span>
          </div>
        </div>
      </div>
    );
  }

  // ========================================
  // MAIN LAYOUT WITH PLAYER MODAL PROVIDER
  // ========================================
  return (
    <NotificationProvider leagueId={leagueId}>
    <PlayerModalProvider
      leagueId={leagueId}
      userTeamId={userTeam?.team_id}
      league={league}
    >
      <div className={dynastyTheme.components.page}>
        {/* Header with User Profile Picture and Player Search */}
        <header 
          className={`px-6 py-4 border-b ${dynastyTheme.components.card.base} ${dynastyTheme.classes.border.light}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <UserProfilePicture />
              
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`lg:hidden ${dynastyTheme.classes.text.white} hover:text-yellow-400`}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              <button
                onClick={() => navigate('/dashboard')}
                className={`flex items-center space-x-3 hover:text-white ${dynastyTheme.classes.transition} ${dynastyTheme.classes.text.neutralLight}`}
              >
                <Crown className={`w-8 h-8 ${dynastyTheme.classes.text.primary}`} />
                <div className="text-left">
                  <div className={`text-xl font-bold ${dynastyTheme.classes.text.white}`}>Dynasty Dugout</div>
                  <div className="text-sm">Return to Dashboard</div>
                </div>
              </button>
            </div>
            
            {/* PLAYER SEARCH - LEAGUE VERSION WITH PRICING & TRANSACTIONS */}
            <PlayerSearchDropdownLeague 
              leagueId={leagueId}
              league={league}
              userTeam={userTeam}
              onPlayerAdded={handlePlayerAdded}
              onPlayerDropped={handlePlayerDropped}
              onOpenPositionModal={handleOpenPositionModal}
            />
            
            <div className="flex items-center space-x-4">
              {/* Warning if there was a non-critical error */}
              {loadError && league && (
                <div className="flex items-center gap-2 px-3 py-1 rounded bg-yellow-500/20 text-yellow-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Limited Mode</span>
                </div>
              )}

              {/* Team Name Display */}
              {userTeam && (
                <div className="hidden md:block text-right">
                  <div className={`font-semibold ${dynastyTheme.classes.text.primary}`}>
                    {userTeam.team_name}
                  </div>
                  <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    Your Team
                  </div>
                </div>
              )}
              
              {/* League Status Badge */}
              {league && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  league.league_status === 'in_season' ? 'bg-green-500/20 text-green-400' :
                  league.league_status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                  league.league_status === 'finalizing' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {league.league_status === 'setup' ? 'Setup Mode' :
                   league.league_status === 'draft' ? 'Draft Mode' :
                   league.league_status === 'finalizing' ? 'Finalizing' :
                   league.league_status === 'in_season' ? 'In Season' :
                   'Setup'}
                </div>
              )}
              
              <span className={`hidden md:block ${dynastyTheme.classes.text.neutralLight}`}>
                Welcome, {userProfile?.firstName || user?.given_name || user?.firstName || 'User'}
              </span>
              <NotificationBell />

              <button
                onClick={handleSignOut}
                className={`flex items-center space-x-2 hover:text-white ${dynastyTheme.classes.transition} ${dynastyTheme.classes.text.neutralLight}`}
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden md:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </header>

        <div className="flex">
          {/* Sidebar */}
          <aside 
            className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static w-72 flex-shrink-0 min-h-screen border-r ${dynastyTheme.classes.transition} duration-300 ease-in-out z-40 ${dynastyTheme.components.card.base} ${dynastyTheme.classes.border.light}`}
            style={{ minWidth: '18rem', maxWidth: '18rem' }}
          >
            <div className="p-4 h-full overflow-y-auto">
              {/* League Info Card */}
              <div className={`mb-6 p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.light}`}>
                <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white} mb-3`}>
                  {league?.league_name || 'Loading...'}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className={dynastyTheme.classes.text.neutralLight}>Teams:</span>
                    <span className={dynastyTheme.classes.text.white}>
                      {teams.length || owners.filter(o => o.team_id).length}/{league?.league_settings?.max_teams || 12}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={dynastyTheme.classes.text.neutralLight}>Your Role:</span>
                    <span className={`font-semibold ${
                      league?.role === 'commissioner' 
                        ? dynastyTheme.classes.text.primary 
                        : dynastyTheme.classes.text.success
                    }`}>
                      {league?.role === 'commissioner' ? 'Commissioner' : 'Owner'}
                    </span>
                  </div>
                  {userTeam && (
                    <div className="flex justify-between">
                      <span className={dynastyTheme.classes.text.neutralLight}>Your Team:</span>
                      <span className={`${dynastyTheme.classes.text.primary} truncate ml-2`}>
                        {userTeam.team_name || 'Team 1'}
                      </span>
                    </div>
                  )}
                  {(league?.salary_settings?.use_salaries) && (
                    <div className="flex justify-between">
                      <span className={dynastyTheme.classes.text.neutralLight}>Salary Cap:</span>
                      <span className={dynastyTheme.classes.text.success}>
                        ${getTotalSalaryCap(league?.salary_settings)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Sections */}
              {getNavigationSections().map((section, sectionIndex) => (
                <div key={sectionIndex} className="mb-6">
                  <h3 className={dynastyTheme.components.sidebar.sectionHeader}>
                    {section.title}
                  </h3>
                  <nav className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      const needsAttention = item.needsAttention;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                          // Clear viewing team when navigating away
                          setViewingTeamId(null);
                          setViewingTeamName(null);
                            // Reset box score team when navigating away from daily-box-scores
                          if (item.id !== 'daily-box-scores') {
                            setSelectedBoxScoreTeamId(null);
                          }
                          // Handle query params if specified
                          if (item.queryParams) {
                            const params = new URLSearchParams();
                            Object.entries(item.queryParams).forEach(([key, value]) => {
                              params.set(key, value);
                            });
                            navigate(`/leagues/${leagueId}?${params.toString()}`, { replace: true });
                          }
                        }}
                          className={`${dynastyTheme.components.sidebar.navItem.base} ${
                            isActive
                              ? dynastyTheme.components.sidebar.navItem.active
                              : needsAttention
                              ? dynastyTheme.components.sidebar.navItem.needsAttention
                              : dynastyTheme.components.sidebar.navItem.inactive
                          }`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1 text-left truncate">{item.label}</span>
                          {isActive && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                          {needsAttention && !isActive && item.badge > 0 && (
                            <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-yellow-500 text-neutral-900 text-xs font-bold flex items-center justify-center">
                              {item.badge}
                            </span>
                          )}
                          {needsAttention && !isActive && !item.badge && (
                            <AlertCircle className="w-4 h-4 flex-shrink-0 text-yellow-400" />
                          )}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              ))}
            </div>
          </aside>

          {/* Mobile Overlay */}
          {sidebarOpen && (
            <div 
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main Content Area */}
          <main className="flex-1 p-6 overflow-x-auto">
            {renderContent()}
          </main>
        </div>
        
        {/* Position Assignment Modal - Rendered at top level */}
        {showPositionModal && playerForAssignment && positionModalCapacity && (
          <PositionAssignmentDropdown
            player={playerForAssignment}
            league={league}
            currentRoster={positionModalRoster}
            onAssign={handleCompletePositionAssignment}
            onCancel={() => {
              setShowPositionModal(false);
              setPlayerForAssignment(null);
              setPositionModalRoster([]);
              setPositionModalCapacity(null);
            }}
            isVisible={showPositionModal}
            onOpenDropShuffle={() => {
              setShowPositionModal(false);
              rosterPlan.open();
            }}
          />
        )}

        {/* RosterPlanModal — drop-shuffle plan flow for top-level adds.
            Per Bible § 4.5.10 atomic drop+add via /plan-add. Wired
            2026-05-15 as part of the § 4.5.20 invariant rollout. */}
        <RosterPlanModal
          candidate={playerForAssignment}
          currentRoster={positionModalRoster}
          state={rosterPlan.state}
          plans={rosterPlan.plans}
          selectedDropPlayer={rosterPlan.selectedDropPlayer}
          selectedPlan={rosterPlan.selectedPlan}
          errorClass={rosterPlan.errorClass}
          errorMessage={rosterPlan.errorMessage}
          infeasibleReason={rosterPlan.infeasibleReason}
          onPickDrop={rosterPlan.requestPlans}
          onChangeDrop={rosterPlan.changeDrop}
          onSelectPlan={rosterPlan.selectPlan}
          onConfirm={rosterPlan.executePlan}
          onCancel={() => {
            rosterPlan.cancel();
            setPlayerForAssignment(null);
            setPositionModalRoster([]);
            setPositionModalCapacity(null);
          }}
          isVisible={rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'}
        />
      </div>
    </PlayerModalProvider>
    </NotificationProvider>
  );
};

export default LeagueDashboard;